import { RestClientService } from '../rest-client.service';
import { ObservableResult } from '../../models/result.model';
import { ContactsDataService } from './contacts-data.service';
import {
  AvailableForConnect, ContactCompany,
  ContactMethod, ContactsHistory,
  ContactsMainModel,
  ContactsModel,
  ContactType, IHealthCheck, ILogOut, RelatedLeadsHistory
} from '../../models/contacts/contacts.model';
import {catchError, switchMap} from 'rxjs/operators';
import {Location} from '@angular/common';
import {environment} from '../../environments/environment';

export class ApiContactsDataService implements ContactsDataService {

  constructor(
    private client: RestClientService
  ) {
  }

  getContactTypes(): ObservableResult<ContactType[]> {
    const url = '/api/Contacts/ContactTypes';
    const method = 'GET';

    return this.client.requestWithConstUrl<ContactType[]>(method, url);
  }

  getContactMethods(): ObservableResult<ContactMethod[]> {
    const url = '/api/Contacts/ContactMethods';
    const method = 'GET';

    return this.client.requestWithConstUrl<ContactMethod[]>(method, url);
  }

  getContacts(id: number): ObservableResult<ContactsModel> {
    const uri = `/api/Contacts/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl<ContactsModel>(method, uri);
  }

  editContact(contact: ContactsMainModel): ObservableResult<any> {
    const uri = `/api/Contacts`;
    const method = 'PUT';

    return this.client.requestWithConstUrl<ContactsModel>(method, uri, contact);
  }

  getContactsByEmail(email: string): ObservableResult<ContactsModel> {
    const uri = `/api/Contacts/ByEmail/${email}`;
    const method = 'GET';

    return this.client.requestWithConstUrl<ContactsModel>(method, uri);
  }

  getAvailableForConnect(leadId: number, search?: string): ObservableResult<AvailableForConnect[]> {
    const uri = `/api/Contacts/AvailableForConnect/${leadId}`;

    if (!search) {
      search = undefined;
    }

    const method = 'GET';

    return this.client.requestWithConstUrl<AvailableForConnect[]>(method, uri, {
      searchTerm: search
    });
  }

  connectContact(contactId: number, leadId: number = null): ObservableResult<void> {
    const uri = `/api/Contacts/Connect/${contactId}?leadId=${leadId}`;
    const method = 'POST';
    const body: any = {};
    if (leadId) {
      body.leadId = leadId;
    }

    return this.client.requestWithConstUrl(method, uri, body);
  }

  connectNewContact(leadId: number, contact: ContactsMainModel): ObservableResult<void> {
    const uri = `/api/Contacts/ConnectNew/${leadId}`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, uri, contact);
  }

  disconnectContact(contactId: number, leadId: number = null): ObservableResult<void> {
    const uri = `/api/Contacts/Disconnect/${contactId}?leadId=${leadId}`;
    const method = 'POST';
    const body: any = {};
    if (leadId) {
      body.leadId = leadId;
    }

    return this.client.requestWithConstUrl(method, uri, body);
  }

  healthCheck(): ObservableResult<IHealthCheck> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', 'contacts/health-check');
    return this.client.get<IHealthCheck>(url).pipe(
      switchMap(({body}) => ObservableResult.ofSuccess(body)),
      catchError(err => ObservableResult.ofError(err, err.status))
    );
  }

  logOut(token: string): ObservableResult<ILogOut> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `contacts/logout?token=${token}`);
    return this.client.get<ILogOut>(url).pipe(
      switchMap(({body}) => ObservableResult.ofSuccess(body)),
      catchError(err => ObservableResult.ofError(err, err.status))
    );
  }

  getContactHistory(id: number): ObservableResult<ContactsHistory[]> {
    const url = `/api/Contacts/ContactHistory/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getRelatedLeads(id: number): ObservableResult<RelatedLeadsHistory[]> {
    const url = `/api/Contacts/RelatedLeads/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getContactCompanies(searchTerm?: string): ObservableResult<ContactCompany[]> {
    const url = `/api/Contacts/Companies`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url, {searchTerm: searchTerm});
  }

}
